"use client";

import { useState, useEffect, useCallback } from "react";
import { GoogleMap, Marker, useJsApiLoader, InfoWindow } from "@react-google-maps/api";
import { X } from "lucide-react";
import { getLocalities } from "@/services/property";
import { capitalizeWords } from "@/helper";

interface LocalityData {
    id: string;
    locality: string;
    count: number;
    lat: number;
    lng: number;
}

const MAP_CONTAINER_STYLE = { width: "100%", height: "100%" };
const DEFAULT_CENTER = { lat: 25.12, lng: 55.20 };
const DEFAULT_ZOOM = 11;

const MAP_STYLES = [ /* (same custom map style JSON) */];

// Gold Tailwind color: #fef08a (Tailwind gold-200)
const GOLD_200_HEX = "#fef08a";

export const PropertyMap = () => {
    const [selectedLocality, setSelectedLocality] = useState<LocalityData | null>(null);
    const [mapRef, setMapRef] = useState<any | null>(null);
    const [localities, setLocalities] = useState<LocalityData[]>([]);
    const [loading, setLoading] = useState(true);

    const googleMapsApiKey =
        import.meta.env.VITE_PUBLIC_GOOGLE_MAPS_API_KEY || process.env.GOOGLE_MAPS_API_KEY || "";

    const { isLoaded, loadError } = useJsApiLoader({
        id: "google-map-script",
        googleMapsApiKey,
        libraries: ["places"],
    });

    const geocode = (address: string) => {
        return new Promise<google.maps.GeocoderResult | null>((resolve) => {
            new google.maps.Geocoder().geocode({ address }, (results, status) =>
                resolve(status === "OK" && results?.[0] ? results[0] : null)
            );
        });
    };

    const fetchMapData = useCallback(async () => {
        try {
            // const res = await fetch(`${import.meta.env.VITE_API_URL}/api/properties/localities`);
            // const res = await fetch(`${import.meta.env.VITE_API_URL}api/properties/localities`);
            // const data = await res.json();

            const data = await getLocalities();

            const geoResults: LocalityData[] = [];
            for (const item of data.localities) {
                const result = await geocode(`${item.locality}, Dubai, UAE`);
                if (!result) continue;

                geoResults.push({
                    id: item._id || item.locality,
                    locality: item.locality,
                    count: item.count,
                    lat: result.geometry.location.lat(),
                    lng: result.geometry.location.lng(),
                });
            }

            setLocalities(geoResults);
        } catch (error) {
            console.error("Map fetch error:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isLoaded) fetchMapData();
    }, [isLoaded, fetchMapData]);

    const onMapLoad = useCallback((map: any) => setMapRef(map), []);

    useEffect(() => {
        if (!mapRef || localities.length === 0) return;

        const bounds = new google.maps.LatLngBounds();
        localities.forEach((loc) => bounds.extend({ lat: loc.lat, lng: loc.lng }));
        mapRef.fitBounds(bounds);
    }, [mapRef, localities]);

    if (loadError)
        return (
            <div className="flex h-screen items-center justify-center bg-red-50 text-red-600">
                Error loading Google Map — check key
            </div>
        );

    if (!isLoaded || loading)
        return (
            <div className="flex h-screen items-center justify-center bg-gray-100 animate-pulse">
                Loading Dubai Map...
            </div>
        );

    return (
        <div className="flex flex-col h-screen overflow-hidden py-16">
            <main className="flex flex-1 relative overflow-hidden">
                <div className="h-full w-full">
                    <GoogleMap
                        mapContainerStyle={MAP_CONTAINER_STYLE}
                        center={DEFAULT_CENTER}
                        zoom={DEFAULT_ZOOM}
                        options={{
                            styles: MAP_STYLES,
                            disableDefaultUI: true,
                            zoomControl: true,
                            streetViewControl: false,
                            clickableIcons: false,
                        }}
                        onLoad={onMapLoad}
                        onClick={() => setSelectedLocality(null)}
                    >
                        {localities.map((loc) => (
                            <Marker
                                key={loc.id}
                                position={{ lat: loc.lat, lng: loc.lng }}
                                onClick={() => setSelectedLocality(loc)}
                                icon={{
                                    path: google.maps.SymbolPath.CIRCLE,
                                    scale: selectedLocality?.id === loc.id ? 20 : 14,
                                    fillColor: GOLD_200_HEX,
                                    fillOpacity: 1,
                                    strokeWeight: 3,
                                    strokeColor: "#ffffff",
                                }}
                                label={{
                                    text: loc.count >= 1000 ? (loc.count / 1000).toFixed(1) + "k" : String(loc.count),
                                    color: "black",
                                    fontWeight: "bold",
                                }}
                            />
                        ))}

                        {selectedLocality && (
                            <InfoWindow
                                position={{ lat: selectedLocality.lat, lng: selectedLocality.lng }}
                                onCloseClick={() => setSelectedLocality(null)}
                            >
                                <div className="max-w-xs p-1">
                                    <h3 className="font-bold text-sm text-gold-800">
                                        {capitalizeWords(selectedLocality.locality)}
                                    </h3>
                                    <p className="text-xs text-gold-600 mt-0.5">
                                        {selectedLocality.count} Active Listings
                                    </p>
                                </div>
                            </InfoWindow>
                        )}
                    </GoogleMap>

                    {!selectedLocality && (
                        <div className="absolute bottom-8 left-8 bg-white p-4 rounded-xl shadow-md max-w-xs text-sm text-gold-700 hidden md:block border">
                            Tap a marker to view locality details.
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default PropertyMap;
